/**
 * Central mapping between backend DisplayStats values and frontend column identifiers.
 *
 * The backend sends PascalCase property names (e.g., "OffensiveRebounds", "Made2Pointers")
 * while the frontend uses human-readable column group names (e.g., "Offensive Rebounds",
 * "Two-point Field Goal"). Multiple backend stats can map to the same column group
 * (e.g., Made2Pointers + Attempted2Pointers → "Two-point Field Goal").
 */

const backendStatToColumnGroup = {
  Minutes: 'Minutes',
  Points: 'Points',
  Pir: 'PIR',
  Rebounds: 'REBOUNDS',
  OffensiveRebounds: 'Offensive Rebounds',
  DefensiveRebounds: 'Defensive Rebounds',
  Assists: 'Assists',
  Steals: 'Steals',
  Blocks: 'Blocks',
  BlocksAgainst: 'Blocks Against',
  Turnovers: 'Turnovers',
  Fouls: 'FOULS',
  FoulsDrawn: 'FOULS',
  Attempted1Pointers: 'FT',
  Made1Pointers: 'FT',
  Missed1Pointers: 'FT',
  Attempted2Pointers: '2FG',
  Made2Pointers: '2FG',
  Missed2Pointers: '2FG',
  Attempted3Pointers: '3FG',
  Made3Pointers: '3FG',
  Missed3Pointers: '3FG',
}

const backendStatToInternalKey = {
  Minutes: 'minutes',
  Points: 'points',
  Pir: 'pir',
  Rebounds: 'totalRebounds',
  OffensiveRebounds: 'offensiveRebounds',
  DefensiveRebounds: 'defensiveRebounds',
  Assists: 'assists',
  Steals: 'steals',
  Blocks: 'blocks',
  Turnovers: 'turnOvers',
  BlocksAgainst: 'blocksAgainst',
  Fouls: 'foulsCommited',
  FoulsDrawn: 'foulsDrawn',
  Attempted1Pointers: 'freeThrows',
  Made1Pointers: 'freeThrows',
  Missed1Pointers: 'freeThrows',
  Attempted2Pointers: 'twoPointer',
  Made2Pointers: 'twoPointer',
  Missed2Pointers: 'twoPointer',
  Attempted3Pointers: 'threePointer',
  Made3Pointers: 'threePointer',
  Missed3Pointers: 'threePointer',
}

const STAT_KEY_ORDER = [
  'minutes', 'points', 'twoPointer', 'threePointer', 'freeThrows',
  'offensiveRebounds', 'defensiveRebounds', 'totalRebounds',
  'assists', 'steals', 'turnOvers', 'blocks', 'blocksAgainst',
  'foulsCommited', 'foulsDrawn', 'pir',
]

const COLUMN_GROUP_ORDER = [
  'Minutes', 'Points', '2FG', '3FG', 'FT',
  'REBOUNDS', 'Offensive Rebounds', 'Defensive Rebounds',
  'Assists', 'Steals', 'Turnovers', 'Blocks', 'Blocks Against',
  'FOULS', 'PIR',
]

/**
 * Converts backend displayStats array to a Set of frontend column group names.
 * Returns null if displayStats is empty/undefined (meaning show all columns).
 * When "Total Rebounds" is present, individual Offensive/Defensive Rebounds groups
 * are removed to avoid duplicate columns.
 */
export function getVisibleColumnGroups(displayStats) {
  if (!displayStats || displayStats.length === 0) return null
  const groups = new Set()
  displayStats.forEach(stat => {
    const group = backendStatToColumnGroup[stat]
    if (group) groups.add(group)
  })
  if (groups.has('REBOUNDS')) {
    groups.delete('Offensive Rebounds')
    groups.delete('Defensive Rebounds')
  }
  return groups
}

/**
 * Converts backend displayStats array to an ordered array of internal stat keys,
 * filtered against the available keys in the table's data.
 * Returns null if displayStats is empty/undefined (meaning show all columns).
 */
export function getVisibleStatKeys(displayStats, availableKeys) {
  if (!displayStats) return null
  if (displayStats.length === 0) return []
  const visibleKeys = new Set()
  displayStats.forEach(stat => {
    const key = backendStatToInternalKey[stat]
    if (key) visibleKeys.add(key)
  })
  return STAT_KEY_ORDER.filter(key => visibleKeys.has(key) && availableKeys.includes(key))
}

// Granular mapping for display cards — each backend stat maps to its own label
// (unlike backendStatToColumnGroup which consolidates related stats into one group).
const backendStatToDisplayLabel = {
  Minutes: 'Minutes',
  Points: 'Points',
  Pir: 'PIR',
  Rebounds: 'Total Rebounds',
  OffensiveRebounds: 'Offensive Rebounds',
  DefensiveRebounds: 'Defensive Rebounds',
  Assists: 'Assists',
  Steals: 'Steals',
  Blocks: 'Blocks',
  BlocksAgainst: 'Blocks Against',
  Turnovers: 'Turnovers',
  Fouls: 'Fouls Committed',
  FoulsDrawn: 'Fouls Drawn',
  Attempted1Pointers: 'Free Throw',
  Made1Pointers: 'Free Throw',
  Missed1Pointers: 'Free Throw',
  Attempted2Pointers: '2P FG',
  Made2Pointers: '2P Made',
  Missed2Pointers: '2P Missed',
  Attempted3Pointers: '3P FG',
  Made3Pointers: '3P Made',
  Missed3Pointers: '3P Missed',
}

const DISPLAY_LABEL_ORDER = [
  'Minutes', 'Points', '2P FG', '2P Made', '2P Missed', '3P FG', '3P Made', '3P Missed', 'Free Throw',
  'Total Rebounds', 'Offensive Rebounds', 'Defensive Rebounds',
  'Assists', 'Steals', 'Turnovers', 'Blocks', 'Blocks Against',
  'Fouls Committed', 'Fouls Drawn', 'PIR',
]

// Mapping for the career-style table which shows made/attempted as separate columns.
const backendStatToCareerColumn = {
  Minutes: 'minutes',
  Points: 'points',
  Pir: 'pir',
  Rebounds: 'totalRebounds',
  OffensiveRebounds: 'offensiveRebounds',
  DefensiveRebounds: 'defensiveRebounds',
  Assists: 'assists',
  Steals: 'steals',
  Blocks: 'blocks',
  BlocksAgainst: 'blocksAgainst',
  Turnovers: 'turnovers',
  Fouls: 'fouls',
  FoulsDrawn: 'foulsDrawn',
  Attempted1Pointers: 'attempted1Pointers',
  Made1Pointers: 'made1Pointers',
  Attempted2Pointers: 'attempted2Pointers',
  Made2Pointers: 'made2Pointers',
  Attempted3Pointers: 'attempted3Pointers',
  Made3Pointers: 'made3Pointers',
}

/**
 * Returns the set of career-table column keys to show, or null when displayStats
 * is empty/undefined (meaning "show all columns").
 */
export function getVisibleCareerColumns(displayStats) {
  if (!displayStats || displayStats.length === 0) return null
  const set = new Set(['gamesPlayed', 'gamesStarted'])
  displayStats.forEach(stat => {
    const col = backendStatToCareerColumn[stat]
    if (col) set.add(col)
  })
  return set
}

// Mapping for federation-style tables which use grouped column keys
// (fg2/fg3/ft consolidate made/attempted/missed into a single column).
const backendStatToFederationLeaf = {
  Minutes: 'minutes',
  Points: 'points',
  Pir: 'pir',
  Rebounds: 'tReb',
  OffensiveRebounds: 'oReb',
  DefensiveRebounds: 'dReb',
  Assists: 'assists',
  Steals: 'steals',
  Blocks: 'blocksFv',
  BlocksAgainst: 'blocksAg',
  Turnovers: 'turnovers',
  Fouls: 'foulsCm',
  FoulsDrawn: 'foulsRv',
  Attempted1Pointers: 'ft',
  Made1Pointers: 'ft',
  Missed1Pointers: 'ft',
  Attempted2Pointers: 'fg2',
  Made2Pointers: 'fg2',
  Missed2Pointers: 'fg2',
  Attempted3Pointers: 'fg3',
  Made3Pointers: 'fg3',
  Missed3Pointers: 'fg3',
}

// Structural columns that should always render in federation tables regardless of mode.
const ALWAYS_VISIBLE_FEDERATION_LEAVES = ['gamesPlayed', 'gamesStarted']

/**
 * Returns the set of federation leaf keys to show, or null when displayStats is
 * empty/undefined (meaning "show all columns" — matches the legacy convention).
 * tReb is added implicitly when oReb or dReb is present, since the federation
 * Rebounds group renders Total alongside Offensive/Defensive.
 */
export function getVisibleFederationLeaves(displayStats) {
  if (!displayStats || displayStats.length === 0) return null
  const set = new Set(ALWAYS_VISIBLE_FEDERATION_LEAVES)
  displayStats.forEach(stat => {
    const leaf = backendStatToFederationLeaf[stat]
    if (leaf) set.add(leaf)
  })
  if (set.has('oReb') || set.has('dReb')) set.add('tReb')
  return set
}

/**
 * Filters STATS_GROUPS / GAME_LOG_GROUPS by the visible-leaves set produced by
 * getVisibleFederationLeaves. Drops groups whose children are all hidden so
 * group headers don't render above empty spans.
 */
export function filterFederationGroups(groups, visibleLeafKeys) {
  if (!visibleLeafKeys) return groups
  return groups
    .map(g => ({ ...g, children: g.children.filter(c => visibleLeafKeys.has(c.key)) }))
    .filter(g => g.children.length > 0)
}

/**
 * Converts backend displayStats array to an ordered array of display labels
 * for display purposes (e.g., in DisplayStatsGrid cards).
 * Uses granular labels (e.g., "2P FG", "2P Made") unlike the table column groups.
 */
export function getDisplayStatLabels(displayStats) {
  if (!displayStats || displayStats.length === 0) return []
  const labels = new Set()
  displayStats.forEach(stat => {
    const label = backendStatToDisplayLabel[stat]
    if (label) labels.add(label)
  })
  return DISPLAY_LABEL_ORDER.filter(name => labels.has(name))
}
